import { supabaseClient } from 'supabase'
import { Database } from 'types/supabase'

export type EventRow = Database['public']['Tables']['events']['Row']

export type QueryEventBySourceApplicationStartParams = {
  sourceApplicationId: number
  startTime: Date
}

/**
 * The occurrence an application already produced for one exact start time.
 *
 * This is the lookup that turns a `(source_application_id, start_time)` unique
 * violation into a recovered event rather than a failed conversion. It returns
 * the FULL row, because the caller hands it back to the curator as the event
 * the retry reused.
 *
 * It returns `null` rather than throwing when nothing matches, and that
 * distinction is load-bearing: `events` carries other unique indexes, so a
 * `23505` with no matching occurrence came from a DIFFERENT constraint and the
 * caller must be able to tell the two apart. Swallowing every `23505` as a
 * successful retry would mark an application processed on a conflict that
 * created nothing.
 *
 * Deliberately not `.single()` — that modifier throws on zero rows, which is
 * the opposite of what this function has to express.
 */
export const queryEventBySourceApplicationStart = async ({
  sourceApplicationId,
  startTime
}: QueryEventBySourceApplicationStartParams): Promise<EventRow | null> => {
  const { data, error } = await supabaseClient
    .from('events')
    .select('*')
    .eq('source_application_id', sourceApplicationId)
    .eq('start_time', startTime.toISOString())

  if (error) {
    throw error
  }

  return data && data.length > 0 ? data[0] : null
}
